import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import useAuthUser from "@/hooks/useAuthUser";
import { isAgentUser } from "@/utils/agentUser";
import {
    getAgentLabelMode,
    setAgentLabelMode,
    toExportLabel,
} from "@/utils/importModuleLabels";

/**
 * Keeps the Ocean Import wording in sync with the logged-in user: agents
 * (roleID 13 / roleName "AGENT") see "Export" everywhere the module says "Import".
 *
 * Translated copy is handled by the i18next post-processor; `moduleLabel` is
 * for the few places that still render hardcoded English.
 *
 * @returns {{ isAgentView: boolean, moduleLabel: (text: string) => string }}
 */
const useImportModuleLabels = () => {
    const authUser = useAuthUser();
    const { i18n } = useTranslation();
    const isAgentView = isAgentUser(authUser);

    useEffect(() => {
        if (getAgentLabelMode() === isAgentView) return;
        setAgentLabelMode(isAgentView);
        // Already-rendered translations are cached, so nudge react-i18next to
        // re-run them with the new labelling.
        i18n.emit("languageChanged", i18n.language);
    }, [isAgentView, i18n]);

    const moduleLabel = useCallback(
        (text) => (isAgentView ? toExportLabel(text) : text),
        [isAgentView],
    );

    return { isAgentView, moduleLabel };
};

export default useImportModuleLabels;
